import React, { useEffect, useState } from 'react';

// Импорт компонентов вкладок
import GoalsTab from './Goals/GoalsTab';
import TasksTab from './Goals/TasksTab';
import StatsTab from './Goals/StatsTab';
import TreeTab from './Goals/TreeTab';
import StrategyPanel from './Goals/StrategyPanel';
import BulkImportModal from './BulkImportModal';

// Импорт хуков
import { useGoalsData } from './Goals/useGoalsData';
import { useGoalOperations } from './Goals/useGoalOperations';
import { useTaskOperations } from './Goals/useTaskOperations';
import { useGoalSteps } from './Goals/useGoalSteps';
import { useStatisticsFilters } from './Goals/useStatisticsFilters';
import { useGoalTree } from './Goals/useGoalTree';
import { useCategoryOperations } from './Goals/useCategoryOperations';
import api from '../services/api';

const Goals: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'goals' | 'tasks' | 'stats' | 'tree' | 'strategy'>('goals');
  const [strategyGoalId, setStrategyGoalId] = useState<number>();
  useEffect(() => {
    const open = (event: Event) => { setStrategyGoalId((event as CustomEvent).detail.goalId); setActiveTab('strategy'); };
    window.addEventListener('strategy-open-goal', open);
    return () => window.removeEventListener('strategy-open-goal', open);
  }, []);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);

  // Загрузка данных
  const { goals, tasks, categories, projects, loading, loadData } = useGoalsData();

  // Слушаем события от AI Ассистента
  useEffect(() => {
    const handleAIGoalsUpdate = (event: any) => {
      console.log('🔄 Получено событие ai-goals-updated, перезагружаю цели...', event.detail);
      loadData();
    };

    const handleAITasksUpdate = (event: any) => {
      loadData();
    };

    window.addEventListener('ai-goals-updated', handleAIGoalsUpdate);
    window.addEventListener('ai-tasks-updated', handleAITasksUpdate);

    return () => {
      window.removeEventListener('ai-goals-updated', handleAIGoalsUpdate);
      window.removeEventListener('ai-tasks-updated', handleAITasksUpdate);
    };
  }, [loadData]);

  // Операции с целями
  const goalOps = useGoalOperations(loadData);

  // Операции с задачами
  const taskOps = useTaskOperations(loadData);

  // Операции с шагами целей
  const stepOps = useGoalSteps(loadData);

  // Фильтры статистики
  const statsFilters = useStatisticsFilters();

  // Дерево целей
  const treeOps = useGoalTree(loadData, goals);

  // Операции с категориями
  const categoryOps = useCategoryOperations(loadData);

  // Функция массового импорта задач
  const handleBulkImport = async (tasksToImport: any[]) => {
    try {
      const response = await api.post('/api/tasks/bulk-import', { tasks: tasksToImport });
      
      if (response.data.success) {
        alert(`✅ Импортировано задач: ${response.data.created}\n${response.data.errors > 0 ? `⚠️ Ошибок: ${response.data.errors}` : ''}`);
        loadData(); // Перезагрузка данных
      }
    } catch (error) {
      console.error('Ошибка импорта задач:', error);
      alert('❌ Ошибка при импорте задач');
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadData();
  }, []);

  if (loading) return <div style={{ textAlign: 'center', padding: '40px' }}>Загрузка...</div>;

  return (
    <div style={{ 
      padding: 'clamp(12px, 3vw, 30px)',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', 
      minHeight: '100vh'
    }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <h1 style={{ fontSize: '36px', fontWeight: '700', color: '#2d3748', margin: 0, textShadow: '2px 2px 4px rgba(0,0,0,0.1)' }}>
          🎯 Цели и Дела
        </h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          {activeTab === 'goals' && (
            <button
              onClick={() => goalOps.setShowGoalForm(!goalOps.showGoalForm)}
              style={{
                padding: '10px 20px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
              }}
            >
              + Цель
            </button>
          )}
          {activeTab === 'tasks' && (
            <>
              <button
                onClick={() => setShowBulkImportModal(true)}
                style={{
                  padding: '10px 20px',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)'
                }}
              >
                📥 Импорт задач
              </button>
              <button
                onClick={() => taskOps.setShowTaskForm(!taskOps.showTaskForm)}
                style={{
                  padding: '10px 20px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
                }}
              >
                + Дело
              </button>
            </>
          )}
        </div>
      </div>

      {/* Вкладки */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '5px',
        marginBottom: '30px',
        background: 'rgba(255, 255, 255, 0.7)',
        padding: '10px',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.08)'
      }}>
        {['goals', 'strategy', 'tasks', 'stats', 'tree'].map((tab) => (
          <button
            key={tab}
            onClick={() => { setActiveTab(tab as any); if (tab !== 'strategy') loadData(); }}
            style={{
              padding: '12px 24px',
              background: activeTab === tab ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
              color: activeTab === tab ? 'white' : '#6b7280',
              border: 'none',
              borderRadius: '8px',
              fontWeight: activeTab === tab ? '600' : '500',
              cursor: 'pointer',
              transition: 'all 0.3s',
              boxShadow: activeTab === tab ? '0 4px 12px rgba(102, 126, 234, 0.4)' : 'none'
            }}
          >
            {tab === 'goals' && '🎯 Цели'}
            {tab === 'strategy' && '🧭 Стратегия'}
            {tab === 'tasks' && '📋 Дела'}
            {tab === 'stats' && '📊 Статистика'}
            {tab === 'tree' && '🌳 Дерево целей'}
          </button>
        ))}
      </div>

      {/* Контент вкладок */}
      {activeTab === 'strategy' && <StrategyPanel initialGoalId={strategyGoalId} onTasksChanged={loadData} />}
      {activeTab === 'tasks' && <StrategyPanel dailyOnly onTasksChanged={loadData} />}
      {activeTab === 'goals' && (
        <GoalsTab
          goals={goals}
          tasks={tasks}
          categories={categories}
          showGoalForm={goalOps.showGoalForm}
          setShowGoalForm={goalOps.setShowGoalForm}
          showCategoryModal={categoryOps.showCategoryModal}
          setShowCategoryModal={categoryOps.setShowCategoryModal}
          editingGoalId={goalOps.editingGoalId}
          setEditingGoalId={goalOps.setEditingGoalId}
          filterGoalStatus={goalOps.filterGoalStatus}
          setFilterGoalStatus={goalOps.setFilterGoalStatus}
          goalForm={goalOps.goalForm}
          setGoalForm={goalOps.setGoalForm}
          handleCreateGoal={goalOps.handleCreateGoal}
          handleEditGoal={goalOps.handleEditGoal}
          handleCancelGoalEdit={goalOps.handleCancelGoalEdit}
          handleDeleteGoal={goalOps.handleDeleteGoal}
          handleCompleteGoal={goalOps.handleCompleteGoal}
          handleActivateGoal={goalOps.handleActivateGoal}
          handleReorderGoals={goalOps.handleReorderGoals}
          loadData={loadData}
          selectedGoalForSteps={stepOps.selectedGoalForSteps}
          setSelectedGoalForSteps={stepOps.setSelectedGoalForSteps}
          stepForm={stepOps.stepForm}
          setStepForm={stepOps.setStepForm}
          handleAddStep={stepOps.handleAddStep}
          handleToggleStep={stepOps.handleToggleStep}
          handleDeleteStep={stepOps.handleDeleteStep}
          handleMoveStepUp={stepOps.handleMoveStepUp}
          handleMoveStepDown={stepOps.handleMoveStepDown}
          expandedStepOnCard={stepOps.expandedStepOnCard}
          setExpandedStepOnCard={stepOps.setExpandedStepOnCard}
          handleAttachTaskToStep={stepOps.handleAttachTaskToStep}
          handleDetachTaskFromStep={stepOps.handleDetachTaskFromStep}
          categoryForm={categoryOps.categoryForm}
          setCategoryForm={categoryOps.setCategoryForm}
          handleCreateCategory={categoryOps.handleCreateCategory}
          handleDeleteCategory={categoryOps.handleDeleteCategory}
          handleUpdateCategoryOrder={categoryOps.handleUpdateCategoryOrder}
        />
      )}

      {activeTab === 'tasks' && (
        <TasksTab
          tasks={tasks}
          goals={goals}
          categories={categories}
          showTaskForm={taskOps.showTaskForm}
          setShowTaskForm={taskOps.setShowTaskForm}
          selectedGoal={taskOps.selectedGoal}
          setSelectedGoal={taskOps.setSelectedGoal}
          filterEnergy={taskOps.filterEnergy}
          setFilterEnergy={taskOps.setFilterEnergy}
          filterSprint={taskOps.filterSprint}
          setFilterSprint={taskOps.setFilterSprint}
          filterStatus={taskOps.filterStatus}
          setFilterStatus={taskOps.setFilterStatus}
          filterCategory={taskOps.filterCategory}
          setFilterCategory={taskOps.setFilterCategory}
          taskForm={taskOps.taskForm}
          setTaskForm={taskOps.setTaskForm}
          editingTaskId={taskOps.editingTaskId}
          setEditingTaskId={taskOps.setEditingTaskId}
          handleCreateTask={taskOps.handleCreateTask}
          handleUpdateTask={taskOps.handleUpdateTask}
          handleEditTask={taskOps.handleEditTask}
          handleCancelEdit={taskOps.handleCancelEdit}
          handleDeleteTask={taskOps.handleDeleteTask}
          handleUpdateTaskStatus={taskOps.handleUpdateTaskStatus}
          handleReorderTasks={taskOps.handleReorderTasks}
          loadData={loadData}
        />
      )}

      {activeTab === 'stats' && (
        <StatsTab
          tasks={tasks}
          goals={goals}
          categories={categories}
          statsFilter={statsFilters.statsFilter}
          setStatsFilter={statsFilters.setStatsFilter}
          statsDateFrom={statsFilters.statsDateFrom}
          setStatsDateFrom={statsFilters.setStatsDateFrom}
          statsDateTo={statsFilters.statsDateTo}
          setStatsDateTo={statsFilters.setStatsDateTo}
        />
      )}

      {activeTab === 'tree' && (
        <TreeTab
          goals={goals}
          projects={projects}
          loadData={loadData}
          showTreeConnectModal={treeOps.showTreeConnectModal}
          setShowTreeConnectModal={treeOps.setShowTreeConnectModal}
          treeConnectSource={treeOps.treeConnectSource}
          setTreeConnectSource={treeOps.setTreeConnectSource}
          handleTreeConnectGoal={treeOps.handleTreeConnectGoal}
          onConnectNodes={treeOps.onConnectNodes}
        />
      )}

      {/* Модальное окно массового импорта */}
      <BulkImportModal
        isOpen={showBulkImportModal}
        onClose={() => setShowBulkImportModal(false)}
        onImport={handleBulkImport}
        goals={goals}
        categories={categories}
      />
    </div>
  );
};

export default Goals;
